<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state
      v-if="!selectedMealCompanies.length"
      icon="mdi-domain"
      :title="$t('no_companies_selected')"
    ></report-state>
    <report-state v-else-if="failed" kind="error" @retry="fetchDailyMoreRestaurants"></report-state>
    <report-state v-else-if="loading.fetchDailyMoreRestaurants" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      :selected="isOpen"
      @sort="sortBy"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-total="{ row }"><b>{{ row.total }}</b></template>

      <template #cell-actions="{ row }">
        <div class="rd-tbl__actions">
          <button
            type="button"
            class="ui-btn ui-btn--sm"
            :disabled="!row.users.length"
            :aria-label="$t('show_employees_who_ordered')"
            :title="$t('show_employees_who_ordered')"
            @click="openUsers(row, $event)"
          >
            <v-icon size="16">mdi-account-multiple-outline</v-icon>
            <span class="rd-btn__label">{{ $t("employees") }}</span>
            <b class="rd-num">{{ row.users.length }}</b>
          </button>
        </div>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportMealsTabReservedMeals from "@/components/report/group-meals/ReportMealsTabReservedMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import hasGroupReservedFetch from "../../../shared/group/hasGroupReservedFetch";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";
import ReportDesktopGroupReservedUsers from "./ReportDesktopGroupReservedUsers.vue";

/**
 * Rezervni obroci (jela iz grupa) na računaru: firma × smena sa ukupnim brojem rezervnih obroka; "Radnici" otvara
 * panel sa radnicima koji su naručili. Firme čita iz zajedničke trake (sve firme restorana), period iz store-a.
 * Nasleđuje štampu i Excel iz starog taba; učitavanje je ovde svoje jer se mora ponoviti i kad se promene firme
 * (stari tab je ponavljao samo pri promeni perioda).
 */
export default {
  name: "ReportDesktopGroupReserved",
  extends: ReportMealsTabReservedMeals,
  mixins: [hasGroupReservedFetch, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("firm"), name: true, sortable: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "end", num: true, zero: true, sortable: true })),
        { key: "total", label: this.$t("total"), align: "end", num: true, sortable: true },
        { key: "actions", label: "", align: "end", headCls: "rd-col-actions" },
      ];
    },
    /** Smene 1–3 uvek, četvrta samo kad neka firma ima rezervne obroke u njoj. */
    visibleShifts() {
      return SHIFTS.filter((shift) => shift.id < 4 || this.companies.some((company) => company[shift.key] > 0));
    },
    allRows() {
      return this.companies.map((company, index) => {
        const row = {
          key: `${company.id ?? index}-${index}`,
          id: company.id,
          name: this.toLatin(company.name),
          total: company.total || 0,
          users: Array.isArray(company.users) ? company.users : [],
        };
        SHIFTS.forEach((shift) => {
          row[shift.key] = company[shift.key] || 0;
        });
        return row;
      });
    },
    rows() {
      const found = this.filters.search
        ? this.customSearch(this.allRows, [{ value: "name" }], this.filters.search)
        : this.allRows;
      return this.sortRows(found);
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + row[shift.key], 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_reserved_meals_data");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("r_o"), value: this.totals.total, tone: "inverse" },
        { key: "companies", label: this.$t("companies"), value: this.rows.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    "loading.fetchDailyMoreRestaurants": {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `reserved-${row.key}`);
    },
    openUsers(row, event) {
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `reserved-${row.key}`,
        root: true,
        title: row.name,
        subtitle: this.$t("show_employees_who_ordered"),
        component: ReportDesktopGroupReservedUsers,
        props: { items: row.users },
      });
    },
    async copyTable() {
      const headers = [this.$t("firm"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [row.name, ...this.visibleShifts.map((shift) => row[shift.key]), row.total]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
